import * as Sentry from "@sentry/react";
import axios from "axios";

import { isStoryOpPending } from "../types/storyOpStatus.enum";
import { STORIES_PARTIAL_UPDATE } from "../store/actions/types";

export default function createStoriesMiddleware() {
  const ctx = {
    pendingStories: [],
    timeoutId: null,
    pendingStoriesAdvanced: [],
    timeoutIdAdvanced: null,
  };

  return ({ dispatch, getState }) =>
    (next) =>
    (action) => {
      // A helper method to return string representation of current
      // pending state for a story. Once we get a simple string, its easy to
      // compare if anything is changed on subsequent poll
      const pendingOpRepr = (story) =>
        story.asyncOperations &&
        story.asyncOperations
          .filter((op) => isStoryOpPending(op.status))
          .map((op) => op.type)
          .join(",");

      const isAdvancedTranscription = (story) => {
        const asyncOps = story.asyncOperations;
        const transcribeOp = asyncOps.find((op) => op.type === "transcribe");
        return transcribeOp?.mode === "advanced";
      };

      const updatePendingStories = (stories) => {
        ctx.pendingStories = stories
          .map((story) => ({
            _id: story._id,
            pending: pendingOpRepr(story),
            isAdvanced: isAdvancedTranscription(story),
          }))
          .filter((x) => x.pending && !x.isAdvanced);

        ctx.pendingStoriesAdvanced = stories
          .map((story) => ({
            _id: story._id,
            pending: pendingOpRepr(story),
            isAdvanced: isAdvancedTranscription(story),
          }))
          .filter((x) => x.pending && x.isAdvanced);
      };

      const clearOutPendingStoryStates = () => {
        for (const story of getState().stories.stories) {
          let storyHasPendingOp = false;
          const asyncOperations =
            !story.asyncOperations ||
            story.asyncOperations.map((op) => {
              if (!isStoryOpPending(op.status)) {
                return op;
              } else {
                storyHasPendingOp = true;
                return { ...op, status: "unknown" };
              }
            });
          if (storyHasPendingOp) {
            const storyData = { _id: story._id, asyncOperations };
            dispatch({ type: STORIES_PARTIAL_UPDATE, payload: storyData });
          }
        }
      };

      const pollPendingStoriesAdvanced = () => {
        // Using setTimeout here rather than setInterval so that we always do 5 seconds BETWEEN
        // polls. If we have several stories and/or the server is slow to respond, hopefully this
        // logic will help us throttle back.
        ctx.timeoutIdAdvanced = setTimeout(async () => {
          try {
            for (const story of ctx.pendingStoriesAdvanced) {
              const res = await axios.get(`/stories/${story._id}?fullLoad=false`, {
                headers: { "X-TS-Polling": 1 },
              });
              const updatedPending = pendingOpRepr(res.data);

              if (story.pending !== updatedPending) {
                // We are not loading all related collection here because we only care about the
                // core story document. However, on the backend 'prepsession' field (and yeah, its
                // the only one!) is overloaded. In story document it is an ObjectId but when we
                // call .loadData() it ends up being prepsession document. Since we are doing a
                // partial update here, let's make sure prep session data doesn't get paved over
                delete res.data.prepsession;

                dispatch({
                  type: STORIES_PARTIAL_UPDATE,
                  payload: res.data,
                });
              }
            }
            ctx.timeoutIdAdvanced = null;
            if (ctx.pendingStoriesAdvanced.length) {
              pollPendingStoriesAdvanced();
            }
          } catch (err) {
            // To make things simple we will assume that nothing in the above loop should
            //  ever fail and this includes HTTP GET /stories/{id}. And if it ever does,
            // it's not a problem with a particular story, but rather the entire app. Therefore,
            // any error will result in full termination of the poll
            console.log(err);
            Sentry.captureException(err);
            clearOutPendingStoryStates();
          }
        }, 60000);
      };

      const pollPendingStories = () => {
        // Using setTimeout here rather than setInterval so that we always do 5 seconds BETWEEN
        // polls. If we have several stories and/or the server is slow to respond, hopefully this
        // logic will help us throttle back.
        ctx.timeoutId = setTimeout(async () => {
          try {
            for (const story of ctx.pendingStories) {
              const res = await axios.get(`/stories/${story._id}?fullLoad=false`, {
                headers: { "X-TS-Polling": 1 },
              });
              const updatedPending = pendingOpRepr(res.data);

              if (story.pending !== updatedPending) {
                // We are not loading all related collection here because we only care about the
                // core story document. However, on the backend 'prepsession' field (and yeah, its
                // the only one!) is overloaded. In story document it is an ObjectId but when we
                // call .loadData() it ends up being prepsession document. Since we are doing a
                // partial update here, let's make sure prep session data doesn't get paved over
                delete res.data.prepsession;

                dispatch({
                  type: STORIES_PARTIAL_UPDATE,
                  payload: res.data,
                });
              }
            }
            ctx.timeoutId = null;
            if (ctx.pendingStories.length) {
              pollPendingStories();
            }
          } catch (err) {
            // To make things simple we will assume that nothing in the above loop should
            //  ever fail and this includes HTTP GET /stories/{id}. And if it ever does,
            // it's not a problem with a particular story, but rather the entire app. Therefore,
            // any error will result in full termination of the poll
            console.log(err);
            Sentry.captureException(err);
            clearOutPendingStoryStates();
          }
        }, 5000);
      };

      const stopStoryPoll = () => {
        clearTimeout(ctx.timeoutId);
        ctx.timeoutId = null;
      };

      const stopStoryPollAdvanced = () => {
        clearTimeout(ctx.timeoutIdAdvanced);
        ctx.timeoutIdAdvanced = null;
      };

      const handleStoriesUpdated = () => {
        updatePendingStories(getState().stories.stories);

        if (ctx.pendingStories.length && !ctx.timeoutId) {
          pollPendingStories();
        } else if (!ctx.pendingStories.length && ctx.timeoutId) {
          stopStoryPoll();
        }

        if (ctx.pendingStoriesAdvanced.length && !ctx.timeoutIdAdvanced) {
          pollPendingStoriesAdvanced();
        } else if (!ctx.pendingStoriesAdvanced.length && ctx.timeoutIdAdvanced) {
          stopStoryPollAdvanced();
        }
      };

      const prevStories = getState().stories.stories;
      const returnValue = next(action);
      const curStories = getState().stories.stories;
      if (prevStories !== curStories) {
        handleStoriesUpdated();
      }
      return returnValue;
    };
}
